// Post-call email follow-up (SPEC §19, 2026-10-02). Wire types shared by the Worker and the dialer.
//
//   GET  /api/calls/:id/email             -> SalesEmailContext   (panel prefill, recording status, what blocks sending)
//   POST /api/calls/:id/email/draft       SalesEmailDraftRequest -> SalesEmailDraft     (Gemini transcript + Claude draft)
//   POST /api/calls/:id/email/send        SalesEmailSendRequest  -> SalesEmailSendResponse (review-first: only on click)
//   GET  /unsubscribe/:token              public page (one button)   POST /unsubscribe/:token  does it (RFC 8058 one-click too)

export type SalesEmailType = 'front_desk' | 'leads_marketing' | 'about' | 'audit' | 'custom'
  // 2026-10-03 (migration 0022): one service each.
  | 'website' | 'local_seo' | 'gbp_reviews' | 'automations';

export const SALES_EMAIL_TYPES: ReadonlyArray<{ key: SalesEmailType; label: string; hint: string }> = [
  // Labels name the stage (the 2026-10-07 offer, ai-agency/web/DECISIONS-2026-10-07-stages.md); the keys stay as they are.
  { key: 'front_desk', label: 'Get Answered (AI receptionist)', hint: 'The AI receptionist that answers their existing number, with the demo line.' },
  { key: 'leads_marketing', label: 'Get Found (website, Google, local SEO)', hint: 'Website, Google profile and reviews, listings and local SEO.' },
  { key: 'website', label: 'Website', hint: 'A fast, mobile-first website, live 14 business days from content approval, with booking or online quoting where it fits.' },
  { key: 'local_seo', label: 'Local SEO (Google search & Maps)', hint: 'Showing up in "near me" searches and the map pack.' },
  { key: 'gbp_reviews', label: 'Google Business Profile & reviews', hint: 'Profile rebuilt and managed, their review link, QR cards and a tap stand, and review replies they approve.' },
  { key: 'automations', label: 'Follow-up & automations', hint: 'Quote follow-ups and appointment reminders: what we set up. Missed-call text back is part of the AI receptionist in the bigger sizes.' },
  { key: 'about', label: 'About Valistix', hint: 'What Valistix does, in general.' },
  { key: 'audit', label: 'Free business audit', hint: 'Only facts the lead engine already measured, each with its source and date.' },
  { key: 'custom', label: 'Custom from this call', hint: 'Built on what they said on this call.' },
];

export const salesEmailTypeLabel = (t: SalesEmailType): string => SALES_EMAIL_TYPES.find((x) => x.key === t)?.label ?? t;

/** Where the draft's facts about the call come from. 'notes' = the setter's notes and the two fields only. */
export type SalesEmailBasis = 'recording' | 'notes';

export interface SalesEmailContext {
  callId: number;
  businessId: number | null;
  businessName: string | null;
  country: 'US' | 'CA' | null;
  /** The call reached a person (answered). Drafting and sending need it. */
  connected: boolean;
  /** The business's address on file (US: may be used; Canada: only if the prospect gave it on this call). */
  prefillEmail: string | null;
  /** An address the prospect said on this call (from an earlier transcript), when found. */
  transcriptEmail: string | null;
  recording: {
    /** available: will be transcribed for the draft; pending: still being filed; transcribed: already done;
     *  not_recorded: no recording (all-party-consent state, failure, or never recorded). */
    status: 'available' | 'pending' | 'transcribed' | 'not_recorded';
    note: string;
  };
  /** Canada (CASL): send only to an address the prospect gave on this call (the setter confirms it). */
  canadaNeedsConsent: boolean;
  /** The From line the email will carry, e.g. "Sam from Valistix <sam@valistix.com>". */
  from: string;
  /** The compliance footer as it will appear (the unsubscribe link is made at send time). */
  footerPreview: string;
  /** The sender's signature as it will appear under the email (plain-text twin of the branded HTML signature; the old
   *  "<First name>" / "Valistix" sign-off when the user has no job title). Added at send time, never part of the body. */
  signature: string;
  /** Why Send is off right now, in one sentence (mailing address missing, do-not-contact, unsubscribed, no business). */
  sendBlocked: string | null;
  /** Why Draft is off right now (not connected, no business, daily limit). */
  draftBlocked: string | null;
  draftsLeftToday: number;
  /** The per-business limit: one email every 7 days. */
  lastSentAt: string | null;
  nextAllowedAt: string | null;
  /** The signed-in user is the owner (may send inside the 7-day limit). */
  ownerCanOverride: boolean;
  sent: SalesEmailSummary[];
}

export interface SalesEmailDraftRequest {
  type: SalesEmailType;
  caredAbout?: string;        // "What did they care about?"
  mention?: string;           // "Anything to mention?"
  notes?: string;             // the setter's wrap-up notes as typed so far
  contactName?: string;       // who they spoke to (greeting), if known
  /** false = draft from the notes only even though a recording exists (e.g. it is still being filed). */
  useRecording?: boolean;
}

export interface SalesEmailDraft {
  draftId: number;
  type: SalesEmailType;
  subject: string;
  body: string;               // editable; no sign-off (the sender's signature is added at send time: `signature`)
  basis: SalesEmailBasis;
  basisNote: string;          // "Based on the call recording." / "Based on your notes only: this call wasn't recorded (...)."
  transcriptEmail: string | null;
  from: string;
  footerPreview: string;
  /** The sender's signature, added under the body at send time (see SalesEmailContext.signature). */
  signature: string;
  /** Free business audit: the facts the draft could use, each with its source and date (empty = none measured). */
  facts: string[];
}

export interface SalesEmailSendRequest {
  draftId: number;
  to: string;
  subject: string;
  body: string;
  /** Canada (CASL): the setter confirms the prospect gave this address on this call and asked for the email. */
  consentOnCall?: boolean;
  /** The owner only: send although this business got an email in the last 7 days. */
  ownerOverride?: boolean;
}

export interface SalesEmailSummary {
  id: number;
  callId: number;
  businessId: number | null;
  type: SalesEmailType;
  typeLabel: string;
  to: string | null;
  subject: string | null;
  fromEmail: string | null;
  sentBy: string | null;      // the sender's name
  status: 'draft' | 'sent' | 'failed';
  sentAt: string | null;
  messageId: string | null;   // Resend id
  followUpDueAt: string | null;
}

export interface SalesEmailSendResponse {
  email: SalesEmailSummary;
  followUp: { taskId: number; dueAt: string; title: string } | null;
}
